import Phaser from 'phaser';

const SKY_TOP = 0x0a0a1f;
const SKY_MID = 0x2e1065;
const SKY_BOT = 0x9f1239;

const BUILDING_DARK = 0x030712;
const BUILDING_MID = 0x0f172a;
const WINDOW_WARM = 0xfde68a;
const WINDOW_COLD = 0x93c5fd;

const NEON_PINK = 0xec4899;
const NEON_CYAN = 0x22d3ee;
const NEON_ORANGE = 0xf97316;
const RAIN_STREAK = 0x4b5563;

const STAR = 0xf4f4f5;

export function drawNightCitySkyBackground(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.55) {
      const u = t / 0.55;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.55) / 0.45;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }

  const rand = mulberry32(2501);
  for (let i = 0; i < 20; i++) {
    const sx = rand() * w;
    const sy = rand() * h * 0.3;
    g.fillStyle(STAR, 0.5);
    g.fillCircle(sx, sy, rand() < 0.2 ? 1.4 : 1);
  }

  const signalCx = w * 0.18;
  const signalCy = h * 0.22;
  g.fillStyle(0xfde68a, 0.35);
  g.fillCircle(signalCx, signalCy, 40);
  g.fillStyle(0xfde68a, 0.85);
  g.fillCircle(signalCx, signalCy, 16);
  const spiderR = 10;
  g.fillStyle(0x0a0f1f, 1);
  g.fillCircle(signalCx, signalCy, spiderR * 0.5);
  for (let side = -1; side <= 1; side += 2) {
    for (let i = 0; i < 3; i++) {
      const ang = (i - 1) * 0.3;
      g.lineStyle(1.5, 0x0a0f1f, 1);
      g.beginPath();
      g.moveTo(signalCx + side * 1, signalCy + ang * 3);
      g.lineTo(
        signalCx + side * spiderR * (0.9 + i * 0.15),
        signalCy + ang * spiderR * 0.6,
      );
      g.strokePath();
    }
  }
}

export function drawNightCitySilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  seed = 42,
) {
  const rand = mulberry32(seed);
  const baselineY = Math.floor(h * 0.78);
  const maxBuildingH = Math.floor(h * 0.55);

  g.fillStyle(BUILDING_DARK, 1);
  g.fillRect(0, baselineY + Math.floor(maxBuildingH * 0.3), w, h - baselineY);

  let x = -10;
  const buildings: { x: number; w: number; h: number; darker: boolean }[] = [];
  while (x < w + 10) {
    const bw = 32 + Math.floor(rand() * 60);
    const bh = Math.floor(maxBuildingH * (0.35 + rand() * 0.95));
    const darker = rand() < 0.45;
    buildings.push({ x, w: bw, h: bh, darker });
    x += bw - Math.floor(rand() * 8);
  }

  for (const b of buildings) {
    const by = baselineY - b.h;
    g.fillStyle(b.darker ? BUILDING_DARK : BUILDING_MID, 1);
    g.fillRect(b.x, by, b.w, h - by);

    const rows = Math.max(3, Math.floor(b.h / 14));
    const cols = Math.max(2, Math.floor(b.w / 10));
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (rand() > 0.55) continue;
        const wx = b.x + 3 + c * Math.floor(b.w / cols);
        const wy = by + 4 + r * 12;
        if (wy > h - 4) continue;
        g.fillStyle(rand() < 0.25 ? WINDOW_COLD : WINDOW_WARM, 0.9);
        g.fillRect(wx, wy, 3, 4);
      }
    }

    if (b.h > maxBuildingH * 0.65 && rand() < 0.35) {
      const isPink = rand() < 0.5;
      const isCyan = !isPink && rand() < 0.5;
      const neon = isPink ? NEON_PINK : isCyan ? NEON_CYAN : NEON_ORANGE;
      const signH = 12;
      const signY = by + Math.floor(b.h * 0.22);
      g.fillStyle(0x0a0f1f, 1);
      g.fillRect(b.x + 4, signY, b.w - 8, signH);
      g.fillStyle(neon, 1);
      g.fillRect(b.x + 6, signY + 2, b.w - 12, signH - 4);
      g.fillStyle(neon, 0.4);
      g.fillRect(b.x + 2, signY - 2, b.w - 4, signH + 4);
    }

    if (b.h > maxBuildingH * 0.8 && rand() < 0.4) {
      const antennaH = 10 + Math.floor(rand() * 12);
      g.fillStyle(BUILDING_DARK, 1);
      g.fillRect(b.x + Math.floor(b.w / 2) - 1, by - antennaH, 2, antennaH);
      g.fillStyle(0xef4444, 1);
      g.fillCircle(b.x + Math.floor(b.w / 2), by - antennaH, 2);
    }
  }

  g.lineStyle(1, RAIN_STREAK, 0.35);
  const rain = mulberry32(seed + 1);
  for (let i = 0; i < Math.floor(w * h / 8000); i++) {
    const rx = rain() * w;
    const ry = rain() * h;
    g.beginPath();
    g.moveTo(rx, ry);
    g.lineTo(rx - 2, ry + 10);
    g.strokePath();
  }
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
